import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/api';

interface ApiCall {
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  error?: string;
  duration?: number;
}

const Diagnostics: React.FC = () => {
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const [authInfo, setAuthInfo] = useState<any>(null);

  const checkHealth = useCallback(async () => {
    const start = Date.now();
    try {
      const response = await fetch(`${API_BASE}/health`);
      const data = await response.json();
      setHealthStatus({
        ...data,
        responseTime: Date.now() - start,
        status: response.status
      });
    } catch (error) {
      setHealthStatus({
        error: error instanceof Error ? error.message : 'Unknown error',
        responseTime: Date.now() - start
      });
    }
  }, []);

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/version`);
      const data = await response.json();
      setVersionInfo(data);
    } catch (error) {
      setVersionInfo({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const user = localStorage.getItem('user');
    
    setAuthInfo({
      hasToken: !!token,
      hasRefreshToken: !!refreshToken,
      hasUser: !!user,
      tokenLength: token?.length || 0,
      userData: user ? JSON.parse(user) : null,
      tokenExpiry: token ? getTokenExpiry(token) : null
    });
  }, []);

  const getTokenExpiry = (token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000).toISOString();
    } catch {
      return 'Invalid token';
    }
  };

  const clearApiCalls = () => {
    setApiCalls([]);
    localStorage.removeItem('diagnostics_api_calls');
  };

  const testApiCall = async () => {
    const start = Date.now();
    const call: ApiCall = {
      timestamp: new Date().toISOString(),
      method: 'GET',
      url: `${API_BASE}/health`
    };

    try {
      const response = await fetch(call.url);
      call.status = response.status;
      call.duration = Date.now() - start;
    } catch (error) {
      call.error = error instanceof Error ? error.message : 'Unknown error';
      call.duration = Date.now() - start;
    }

    const updated = [call, ...apiCalls].slice(0, 20); // Keep last 20 calls
    setApiCalls(updated);
    localStorage.setItem('diagnostics_api_calls', JSON.stringify(updated));
  };

  useEffect(() => {
    // Load stored API calls from localStorage
    const stored = localStorage.getItem('diagnostics_api_calls');
    if (stored) {
      try {
        setApiCalls(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored API calls:', e);
      }
    }

    // Check health and version
    checkHealth();
    checkVersion();
    checkAuthStatus();
  }, [checkHealth, checkVersion, checkAuthStatus]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">System Diagnostics</h1>
            
            {/* API Base */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">API Configuration</h2>
              <div className="bg-gray-50 p-4 rounded-md">
                <p><strong>API Base:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{API_BASE}</code></p>
                <p><strong>Override:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{localStorage.getItem('API_BASE_OVERRIDE') || 'None'}</code></p>
              </div>
            </div>

            {/* Health Status */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Backend Health</h2>
              <div className="bg-gray-50 p-4 rounded-md">
                {healthStatus ? (
                  <pre className="text-sm">{JSON.stringify(healthStatus, null, 2)}</pre>
                ) : (
                  <p>Loading...</p>
                )}
                <button
                  onClick={checkHealth}
                  className="mt-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  Refresh Health
                </button>
              </div>
            </div>

            {/* Version Info */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Version Info</h2>
              <div className="bg-gray-50 p-4 rounded-md">
                {versionInfo ? (
                  <pre className="text-sm">{JSON.stringify(versionInfo, null, 2)}</pre>
                ) : (
                  <p>Loading...</p>
                )}
              </div>
            </div>

            {/* Auth Status */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Status</h2>
              <div className="bg-gray-50 p-4 rounded-md">
                {authInfo ? (
                  <pre className="text-sm">{JSON.stringify(authInfo, null, 2)}</pre>
                ) : (
                  <p>Loading...</p>
                )}
              </div>
            </div>

            {/* API Calls */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Recent API Calls</h2>
                <div className="space-x-2">
                  <button
                    onClick={testApiCall}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    Test API Call
                  </button>
                  <button
                    onClick={clearApiCalls}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Clear History
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
                {apiCalls.length > 0 ? (
                  <div className="space-y-2">
                    {apiCalls.map((call, index) => (
                      <div key={index} className="border-b border-gray-200 pb-2">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {call.method} {call.url}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(call.timestamp).toLocaleString()}
                              {call.duration && ` • ${call.duration}ms`}
                            </p>
                          </div>
                          <div className="text-right">
                            {call.status && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                call.status >= 200 && call.status < 300 
                                  ? 'bg-green-100 text-green-800'
                                  : call.status >= 400
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {call.status}
                              </span>
                            )}
                            {call.error && (
                              <p className="text-xs text-red-600 mt-1">{call.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No API calls recorded yet</p>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">How to use this page:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Check API Base URL is pointing to your Render backend</li>
                <li>• Verify Backend Health shows "connected" database status</li>
                <li>• Test API Call button makes a live request to /health</li>
                <li>• Authentication Status shows your current login state</li>
                <li>• Recent API Calls tracks the last 20 requests (stored in localStorage)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Diagnostics;

